import React from 'react';
import useTranslation from 'next-translate/useTranslation';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import styled from '@mui/system/styled';
import theme from '@/mui/theme';
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import {getIconComponentByName} from '@/utils/icons-map';
import { sanitize } from '@/utils/miscellaneous';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';

export interface ProductOverlayTableProps {
  title?: string;
  iconList?: {
    icon: string;
    iconTooltip: string;
  }[];
  table: {
    title?: string;
    desc: string;
  }[];
}

const StyledExternalTitle = styled(Typography)({
  color: "#182641",
  fontSize: "24px",
  fontWeight: "bold",
  letterSpacing: "-0.1px",
  lineHeight: "30px",
  marginBottom: "11px",
});

const StyledBoxIcon = styled(Box)({
  [theme.breakpoints.up('sm')]: {
    paddingRight: '5vw',
    marginBottom: '16px',
  },
  display: 'flex',
  flexWrap: 'wrap',
  marginTop: '5px',
  marginBottom: '50px',
});

const HtmlTooltip = styled(({ className, ...rest }: TooltipProps) => (
  <Tooltip {...rest} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#ffffff',
    borderRadius: '5px',
    color: '#182641',
    maxWidth: '220px',
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0',
    padding: '14px',
    boxShadow: '0 7px 20px 0 rgba(28, 28, 28, 0.43)',
    border: '0',
    '.MuiTooltip-arrow': {
      '&::before': {
        border: '0!important',
        backgroundColor: '#ffffff!important',
        boxShadow: 'unset!important',
        position: 'relative',
        marginBottom: '-1px',
        zIndex: '-99!important'
      }
    }
  },
})) as typeof Tooltip;

const StyledButton = styled(Button)({
  padding: '0',
  margin: '0 10px 10px 0',
  minWidth: '0',
  cursor: 'default',
  'svg': {
    pointerEvents: 'none',
  },
  '&:hover': {
    backgroundColor: 'unset'
  }
});

const StyledTableContainer = styled(TableContainer)({
  '.MuiTableRow-root': {
    borderBottom: '1px solid #f5f5f5',
    'td': {
      padding: '12px 0',
      border: '0',
      fontSize: '16px',
      letterSpacing: '0.1px',
      color: '#000000',
      '&:first-of-type': {
        fontWeight: '600',
        [theme.breakpoints.up('sm')]: {
          width: '335px',
        }
      }
    }
  }
});

const ProductOverlayTable: React.FC<ProductOverlayTableProps> = ({ title, iconList, table }) => {
  const {t} = useTranslation('common');

  return <Grid container rowSpacing={0}>
    <Grid item xs={12} sx={{ marginTop: { sm: '35px', xs: '50px' } }}>
      {title &&
        <StyledExternalTitle>{title}</StyledExternalTitle>
      }
      {iconList &&
        <StyledBoxIcon>
          {iconList.map((singleIcon, index) => {
            return <HtmlTooltip
              key={index}
              title={<div dangerouslySetInnerHTML={{__html: sanitize(singleIcon.iconTooltip)}} />}
              placement="top"
              arrow
            >
              <StyledButton>{getIconComponentByName(singleIcon.icon)}</StyledButton>
            </HtmlTooltip>
          })}
        </StyledBoxIcon>
      }
      {table && table.length > 0 && 
        <StyledTableContainer>
          <Table aria-label={t('product-detail-dimension-table', {}, {default: 'product detail dimension table'})}>
            <TableBody>
              {table.map((row, index) => {
                return <TableRow key={index}>
                  <TableCell sx={{ minWidth: '200px' }}>
                    {row.title}
                  </TableCell>
                  <TableCell>
                      <span dangerouslySetInnerHTML={{__html: sanitize(row.desc)}}/>
                  </TableCell>
                </TableRow>
              })}
            </TableBody>
          </Table>
        </StyledTableContainer>
      }
    </Grid>
  </Grid>
}

export default ProductOverlayTable;
